import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button';
import { RHFNestedFormProvider } from '@openx/components-core/lib/Form/context/RHFNestedFormContext';
import { WhenLoaded } from '@openx/components-core/lib/Loader/WhenLoaded';
import { PackageForm } from '@openx/components-package/components/PackageForm/PackageForm';
import type { UnionOfSectionNames } from '@openx/components-package/components/PackageForm/PackageForm.sections';
import { usePackageEditPermissions } from '@openx/components-package/utils/hooks/usePackageEditPermissions';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import { type DealFormDataWithTargeting, dealFormField } from '@openx/types/deal';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useDealFormContext } from '../../../DealForm.context';
import type { LocationState } from '../../../DealForm.types';
import { SelectedPackageDetails } from './SelectablePackage/components/SelectedPackageDetails';
import { PackageSelect } from './SelectablePackage/PackageSelect';

type ExistingTargetingPackageTabProps = {
  currency: string;
};

export const ExistingTargetingPackageTab = ({ currency }: ExistingTargetingPackageTabProps) => {
  const { t } = useTranslation();
  const formMethods = useFormContext();
  const { pckg, pckgClear, pckgInitialValues, pckgIsLoading } = useDealFormContext();
  const observedPackageUid = useWatch<DealFormDataWithTargeting>({ name: 'package_uid' });
  const observedAccountUid = useWatch<DealFormDataWithTargeting>({ name: 'account_uid' });

  const onPackageRemove = useCallback(() => {
    pckgClear(true);
  }, [pckgClear]);

  /** RHF defaultValues object is mutable, therefore clone the object data */
  const [initialValues, setInitialValues] = useState(() => structuredClone(pckgInitialValues));

  useEffect(() => {
    setInitialValues(structuredClone(pckgInitialValues));
  }, [pckgInitialValues]);

  /** Clear Error when leaving this tab */
  useEffect(() => {
    return () => {
      formMethods.clearErrors(dealFormField.PACKAGE_UID);
    };
  }, [formMethods.clearErrors]);

  /** Error for PackageUID field, prevents from submitting form when package is not selected */
  useEffect(() => {
    if (!pckg?.uid) {
      formMethods.setError(dealFormField.PACKAGE_UID, { type: 'required', message: t('Package is required') });
    } else {
      formMethods.clearErrors(dealFormField.PACKAGE_UID);
    }
  }, [pckg?.uid, formMethods.setError, formMethods.clearErrors, t]);

  const locationState: LocationState = useLocation().state;
  /** Package attached via "Use Shared Targeting" is a read-only reference to another deal's
   * package: edits made here are never submitted (create mutation always sends an empty `package`),
   * so it must never render as an editable form regardless of the viewer's package permissions. */
  const isReusedSharedTargetingPackage = !!locationState?.useSharedTargeting;

  const { isLocked } = usePackageEditPermissions(pckg || undefined);
  const isPackageEditDisabled = isLocked || isReusedSharedTargetingPackage;

  const render = () => (
    <>
      {observedPackageUid && (
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Grid size={10}>
            <Typography
              variant="h3"
              data-test="package-name"
              sx={{ overflowWrap: 'anywhere' }}
            >{`${t('Package')}: ${pckgInitialValues?.name}`}</Typography>
          </Grid>

          <Grid
            size={2}
            container
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <Button
              sx={{ color: theme => theme.palette.error.main, marginLeft: theme => theme.spacing(2) }}
              data-test="remove-package-button"
              disabled={formMethods.formState.isSubmitting}
              onClick={onPackageRemove}
              variant="text"
              color="secondary"
            >
              {t('Remove')}
            </Button>
          </Grid>
        </Grid>
      )}

      <Grid size={RIGHT_COLUMN_WIDTH + 2.75}>{!pckg?.uid && <PackageSelect />}</Grid>

      {observedPackageUid && initialValues && isPackageEditDisabled && (
        <Fragment key={JSON.stringify(initialValues)}>
          <RHFNestedFormProvider<DealFormDataWithTargeting, UnionOfSectionNames>>
            <SelectedPackageDetails packageState={initialValues} />
          </RHFNestedFormProvider>
        </Fragment>
      )}

      {observedPackageUid && initialValues && !isPackageEditDisabled && (
        <Fragment key={JSON.stringify(initialValues)}>
          <RHFNestedFormProvider<DealFormDataWithTargeting, UnionOfSectionNames>
            namespace="targeting"
            sections={{
              Targeting: {
                contentOnly: true,
              },
            }}
            formConfig={{
              noActions: true,
              noWrapper: true,
            }}
          >
            <PackageForm
              initialValues={initialValues}
              isSubmitting={formMethods.formState.isSubmitting}
              currency={currency}
              accountUid={observedAccountUid}
            />
          </RHFNestedFormProvider>
        </Fragment>
      )}
    </>
  );

  return <WhenLoaded loading={pckgIsLoading}>{render()}</WhenLoaded>;
};
